/**
 * 自定义侧边栏购物车 - 劫持阶段
 * ============================================================================
 * 本文件做四件事：
 *   1) 劫持 form 原生提交：阻断 Shopify 表单加购的页面跳转，改用 fetch 重发
 *   2) 劫持 window.fetch：捕获 SPA / 主题脚本中通过 fetch 发起的 cart API
 *   3) 劫持 XMLHttpRequest：捕获老脚本（jQuery.ajax 等）发起的 cart API
 *   4) 劫持 <a href="/cart"> 点击：阻止跳转到 cart 页，改为打开侧边栏
 *
 * 1-3 命中 /cart/(add|change|update|clear) 时派发两类自定义事件：
 *   - myCusCart:AddStart  仅 add 类型，请求"发出时"立即触发，用于打开 sidebar + loading
 *   - myCusCart:Update    所有类型，请求"返回后"触发；非 add 防抖 200ms 合并连续操作
 *
 * Vue 端通过监听上述事件把侧边栏的开/关、loading 显示串起来。
 * ============================================================================
 */

// =========================================================================
// [IIFE 包裹] 用立即执行函数把整个脚本的顶层声明限制在函数作用域内。
// -------------------------------------------------------------------------
// 起因：本文件在部分页面（如 PageFly 营销页）会与其它同样使用 Vue3 的第三方脚本
//   （例如 CartBite）共存，那些脚本在全局作用域用 let/const 声明了 createApp。
//   而本文件原先在全局用 `var { createApp, ... } = Vue` 重复声明同名标识符，
//   触发编译期错误 —— SyntaxError: Identifier 'createApp' has already been declared —
//   该错误发生在解析阶段，会导致整个文件被浏览器直接丢弃、一行都不执行：
//   Vue 从未挂载 → 模板里的 [[ ]] 插值与 :class / @click 指令原样残留在 DOM 中。
// 用 IIFE 隔离后，createApp / ref / computed / watch 等全部降级为函数作用域局部变量，
//   与页面上其它脚本的全局同名声明彻底互不干扰。
// 对外契约不变：仍通过 window.myCustomCartOpen / window.myCustomCartClose 以及
//   window.fetch / XMLHttpRequest.prototype 的劫持向外暴露，包裹不影响这些行为。
// 注：IIFE 内部代码保持原有缩进（未额外内缩一层），以避免产生大范围无意义 diff。
// =========================================================================
(function () {

// =========================================================================
// 1) 监听购物车 API 调用：拦截 form / fetch / XHR 三种加购方式
// =========================================================================
function myCustomCartListenEvent() {
  // 仅匹配带 .js 后缀的标准 JSON 接口；form 路径在 1.1 内单独再做一次宽松匹配
  const CART_URL_PATTERN = /\/cart\/(add|change|update|clear)\.js/;
  // Update 事件防抖句柄：连续 quantity 调整等场景下，避免高频触发下游刷新
  let _debounceTimer = null;

  // add 类型："请求刚发出"就要让用户感知（打开 sidebar + loading），不等响应
  function dispatchCartAddStart() {
    document.dispatchEvent(new CustomEvent('myCusCart:AddStart'));
  }

  // 所有类型："请求返回后"派发，下游用于刷新 cart.json
  // add 不防抖（用户点一次就期望立刻看到结果）；其他类型 200ms 合并
  function dispatchCartUpdate(type) {
    clearTimeout(_debounceTimer);
    const delay = type === 'add' ? 0 : 200;
    _debounceTimer = setTimeout(() => {
      document.dispatchEvent(new CustomEvent('myCusCart:Update', { detail: { type: type || 'unknown' } }));
    }, delay);
  }

  // ---------------------------------------------------------------------
  // 1.1 拦截 form 原生提交
  //     某些主题/产品页用 <form action="/cart/add" method="post"> 直接提交，
  //     默认行为是"提交后跳转到 /cart 页"——这与 sidebar 体验冲突，必须拦下。
  //     拦下后改用 fetch 以 JSON 方式重发，请求体仍走原 FormData 不丢字段。
  //     用 capture 阶段绑定，确保在主题/App 自己的 submit handler 之前先拿到事件。
  // ---------------------------------------------------------------------
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !form.action) return;
    try {
      const url = new URL(form.action, window.location.origin);
      // 这里宽松匹配（不强制 .js），因为 form action 经常写成 "/cart/add"
      if (CART_URL_PATTERN.test(url.pathname + '.js') || /\/cart\/(add|change|update|clear)/.test(url.pathname)) {
        e.preventDefault();
        e.stopPropagation();
        // form 的 add 在"提交瞬间"就派发，避免等 fetch 响应造成 sidebar 弹出延迟
        if (/\/cart\/add/.test(url.pathname)) {
          dispatchCartAddStart();
        }
        const formData = new FormData(form);
        // Shopify 的 cart API 必须带 .js 才返回 JSON，否则会重定向到 cart 页
        const fetchUrl = url.pathname.endsWith('.js') ? url.pathname : url.pathname + '.js';
        fetch(fetchUrl, {
          method: form.method && form.method.toUpperCase() === 'GET' ? 'GET' : 'POST',
          headers: { 'Accept': 'application/json' },
          body: form.method && form.method.toUpperCase() === 'GET' ? undefined : formData,
        })
          .then(function (res) { return res.json(); })
          .then(function () {
            const _actionMatch = fetchUrl.match(/\/cart\/(add|change|update|clear)/);
            dispatchCartUpdate(_actionMatch ? _actionMatch[1] : 'unknown');
          })
          .catch(function (err) {
            // 不抛出，避免把错误冒泡到全局；线上只打日志，sidebar 仍可手动关闭
            console.log('myCustomCartListenEvent form submit error:', err);
          });
      }
    } catch (_) {
      // URL 构造失败时静默放行，不影响其他 form 提交
    }
  }, true);

  // ---------------------------------------------------------------------
  // 1.2 拦截 fetch
  //     保留原 fetch 引用并替换全局；命中 cart 路径时旁路派发事件，
  //     对返回值不做任何包装（仍是原 Promise），调用方的 .then/.catch 不受影响。
  //
  //     注意：若其他 App 在我们之后又包装了 window.fetch，他们的 wrap 内部
  //     会调到我们的 wrap，事件仍能触发。
  //     若有 App 用 hard-replace（不保留原 fetch）方式覆盖，会绕过我们——
  //     这种 App 较少见，遇到再单独处理。
  // ---------------------------------------------------------------------
  const _originalFetch = window.fetch;
  window.fetch = function (input, init) {
    // input 可能有三种形态，分别取 URL 字符串：
    //   - string   直接使用
    //   - Request  取 .url（Request 对象的标准字段）
    //   - URL      取 .href（URL 对象没有 .url，只有 .href / toString()）
    // 历史 bug：早期只判断了 string + .url，URL 对象会被识别成 ''，导致部分
    // App（赠品插件、礼品规则等）若用 fetch(new URL('/cart/update.js', origin))
    // 调用会绕过劫持。这里多兜一层 .href 修掉。
    const url = (typeof input === 'string') ? input : (input?.url || input?.href || '');
    const result = _originalFetch.apply(this, arguments);
    if (CART_URL_PATTERN.test(url)) {
      const _fetchActionMatch = url.match(/\/cart\/(add|change|update|clear)/);
      const _fetchActionType = _fetchActionMatch ? _fetchActionMatch[1] : 'unknown';
      if (_fetchActionType === 'add') {
        dispatchCartAddStart();
      }
      // 复用原 Promise，不影响调用方的 .then 链；catch 吞掉避免 unhandled rejection
      result.then(() => {
        dispatchCartUpdate(_fetchActionType);
      }).catch(() => {});
    }
    return result;
  };

  // ---------------------------------------------------------------------
  // 1.3 拦截 XMLHttpRequest
  //     兼容老脚本（如使用 jQuery.ajax / Prototype 的旧 App）。
  //     在 open 时记录 url 与 action 类型到 xhr 实例上，send 时按需派发。
  //     原生 open/send 完整透传，不影响调用方逻辑。
  // ---------------------------------------------------------------------
  const _originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    // 把 url / actionType 暂存到当前 xhr 实例上，留给 send 时读取
    this._myCusCartUrl = url || '';
    const _xhrActionMatch = (url || '').match(/\/cart\/(add|change|update|clear)/);
    this._myCusCartActionType = _xhrActionMatch ? _xhrActionMatch[1] : 'unknown';
    return _originalXHROpen.apply(this, arguments);
  };

  const _originalXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (CART_URL_PATTERN.test(this._myCusCartUrl || '')) {
      const _xhrType = this._myCusCartActionType || 'unknown';
      if (_xhrType === 'add') {
        dispatchCartAddStart();
      }
      // 监听本次请求的 load 事件，避免污染调用方挂在 onload 上的回调
      this.addEventListener('load', function () {
        dispatchCartUpdate(_xhrType);
      });
    }
    return _originalXHRSend.apply(this, arguments);
  };
}

// =========================================================================
// 2) 监听 <a href="/cart"> 点击：阻止跳转，打开自定义侧边栏
//    与 1-3 不同，这里劫持的是"用户主动想看购物车"的入口，不是 cart API；
//    因此不派发 myCusCart:Update（购物车数据未变），只调用 sidebar 打开方法。
// =========================================================================
function myCustomCartListenAnchorClickFn() {
  // /cart/add | /cart/change | /cart/update | /cart/clear 这些是 API 路径，
  // 不是真正的"购物车页面"链接，已由方法 1-3 覆盖；这里需要排除掉。
  const CART_API_SUBPATH = /^\/cart\/(add|change|update|clear)\b/;

  document.addEventListener('click', function (e) {
    // 已被其他脚本调用 preventDefault，说明上游已决定如何处理，不再干预
    if (e.defaultPrevented) return;
    // 只处理"普通左键单击"：
    //   - button !== 0：中键 / 右键
    //   - 任一修饰键：用户希望新标签页 / 新窗口 / 下载——必须保留浏览器原生行为
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // 点击点可能在 <a> 内的 <img> / <span>，用 closest 向上找
    const anchor = e.target && e.target.closest && e.target.closest('a[href]');
    if (!anchor) return;
    // 显式声明跨窗口的链接放行（_blank 新标签、_top/_parent 跳出 iframe）
    if (anchor.target && (anchor.target === '_blank' || anchor.target === '_top' || anchor.target === '_parent')) return;

    let urlObj;
    try {
      // 用 anchor.href（DOM 解析后的绝对 URL），而不是 getAttribute('href')，
      // 这样 "/cart" / "cart" / "https://shop.com/cart" 都能统一比较
      urlObj = new URL(anchor.href, window.location.origin);
    } catch (_) {
      return;
    }
    // 跨域链接（如指向其他子站的 /cart）放行，避免误伤
    if (urlObj.origin !== window.location.origin) return;
    // 排除 cart API 子路径
    if (CART_API_SUBPATH.test(urlObj.pathname)) return;
    // 仅命中 /cart 或 /cart/（允许 ?discount=xxx 这类 query string）
    const pathNoTrailingSlash = urlObj.pathname.replace(/\/+$/, '') || '/';
    if (pathNoTrailingSlash !== '/cart') return;

    // 命中：阻止跳转 + 阻止冒泡（防止主题/App 的兜底跳转）
    e.preventDefault();
    e.stopPropagation();
    // Vue 应用挂载时把 open 函数写到 window 上；理论上 click 早于 mount 发生的
    // 概率极低（用户至少要看到页面才会点击），但仍判空兜底
    if (typeof window.myCustomCartOpen === 'function') {
      window.myCustomCartOpen();
    }
  }, true); // capture 阶段：早于主题/App 自己的 click handler 拿到事件
}

// =========================================================================
// 2.1) 监听 .mx-request-button-drawer 点击：点击时收起 sidebar
//     - 该按钮由第三方询价/见積插件（mx-quote）异步注入到模板占位符
//       <div id="drawer-cart-mx-quote">…</div> 内，挂载时机不可预测，
//       直接 querySelector + addEventListener 极易在按钮还未注入时绑定失败，
//       因此采用文档级事件委托（与方法 2 anchor 劫持同思路）。
//     - 仅作"附带收起 sidebar"的副作用：
//         · 不 preventDefault：插件自己的弹窗 / 跳转逻辑需照常进行
//         · 不 stopPropagation：插件其它内部 handler 不能被吞掉
//     - capture 阶段：兜底防止插件在 bubble 阶段 stopPropagation 导致我们错过事件
//     - 普通左键单击才处理：中键 / 修饰键场景多为新窗口语义，与 sidebar 关闭无关
// =========================================================================
function myCustomCartListenInquiryBtnClickFn() {
  document.addEventListener('click', function (e) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = e.target;
    if (!target || !target.closest) return;
    // 命中按钮自身或其子节点（图标 / 文字 span）都算
    const btn = target.closest('button.mx-request-button-drawer');
    if (!btn) return;
    // Vue 应用 mount 后会把 close 挂到 window；理论上点击时 sidebar 已存在，
    // 这里仍判空兜底，避免极端时序下报错
    if (typeof window.myCustomCartClose === 'function') {
      window.myCustomCartClose();
    }
  }, true);
}

// =========================================================================
// 3) 启动
//
// 3.1 劫持注册：脚本顶层立即执行，不再等 DOMContentLoaded
//     -------------------------------------------------------------------
//     旧版把这两个调用包在 DOMContentLoaded 里，相当于在 defer 脚本执行时机
//     的基础上又延后一次。后果是：<head> 中其它 App 脚本（同步执行）若在我们
//     之前缓存了 window.fetch 的引用（const _f = window.fetch），它们后续发
//     起的 cart API 调用就会绕过我们的劫持，导致 cartData 不刷新——曾出现
//     "赠品插件 /cart/update.js 后侧边栏看不到赠品"的 bug。
//
//     现在改为：脚本一加载就立刻替换 window.fetch / XHR.prototype.open / 注册
//     submit & click 监听。虽然此脚本仍是 defer 加载，无法早于 <head> 同步
//     脚本，但至少不会再因 DOMContentLoaded 这一层多出可避免的延迟。
//     若仍有 App 在我们之前缓存了 fetch，最终治本需把这段劫持代码 inline 进
//     <head> 顶部、跑在所有 App 之前——后续如有需要再做。
//
//     此时调用安全性：
//       - document 一定存在（脚本能跑就有 document），addEventListener 安全
//       - myCustomCartListenAnchorClickFn 监听 document 上的 click，
//         不依赖具体 DOM 节点
//       - myCustomCartListenEvent 仅 patch window.fetch / XHR.prototype +
//         监听 document submit，同样不依赖具体节点
// =========================================================================
myCustomCartListenEvent();
myCustomCartListenAnchorClickFn();
myCustomCartListenInquiryBtnClickFn();

// =========================================================================
// 3.2 sidebar 容器显隐：visibility:hidden → visible 的延迟切换
//     避免页面初始化期间 Vue 模板字面量（[[ ... ]]）短暂闪现给用户。
//     此处依赖 #myCustomCartBundleId 节点存在，必须等 DOM 就绪。
// =========================================================================
document.addEventListener('DOMContentLoaded', function () {
  setTimeout(function () {
    var appDom = document.getElementById('myCustomCartBundleId');
    if (appDom) {
      appDom.classList.add('isshow');
    }
  }, 100);
});

// =========================================================================
// 4) Vue3 应用：仅承载 sidebar 开关与 loading 状态，业务逻辑分阶段加入
// =========================================================================
var { createApp, ref, computed, onMounted, onUnmounted, watch } = Vue;

createApp({
  // Shopify Liquid 模板里 {{ }} 已被 Liquid 占用，所以 Vue 改用 [[ ]] 做插值
  compilerOptions: {
    delimiters: ['[[', ']]'],
  },
  setup() {
    // ---------- sidebar 状态 ----------
    // sidebar 显隐
    const isShowCart = ref(false);
    // 加购请求"发出后到响应前"的中间态，控制 loading 蒙层
    const isShowLoading = ref(false);

    // ---------- Liquid → window → Vue 的数据桥接 ----------
    // 这些数据由 my-custom-cart.liquid 中的 <script> 注入到 window，
    // 在 Vue setup 阶段读一次包成 ref。包成 ref 的好处：
    //   1) 模板里可直接用，且后续若需替换（例如重新拉取 cart.json）能保持响应式；
    //   2) 把"全局变量依赖"集中在此处，业务代码不再直接访问 window，方便测试与替换。
    //
    // 用 ?? {} / ?? null 兜底，避免后台尚未配置元字段时 undefined 引发模板报错。

    // 后台元字段：购物车统一配置（业务开关、阈值、文案、折扣码映射等全部归一到此对象）
    const customCartCommonConfig = ref(window.customCartCommonConfig ?? {});
    // 后台元字段：推荐产品折扣徽章配置（{ [pid]: { [vid]: { enabled, discount, ... } } }）
    // 在推荐卡右上角渲染 "XX% OFF" 时使用；未配置 / 未 enabled 的产品不展示徽章
    const customCartDiscountConfig = ref(window.customCartDiscountConfig ?? {});
    // 首屏 cart 快照（item_count / items / total_price 等）
    // 后续加购/改数量后，会用 fetch('/cart.js') 的结果覆盖此 ref
    const cartData = ref(window.myCustomCartInitialCart ?? null);

    const openCartFn = () => { isShowCart.value = true; };
    const closeCartFn = () => { isShowCart.value = false; };

    // ---------- sidebar 打开/关闭时锁定/还原外层页面滚动 ----------
    // 默认情况下，sidebar 用 fixed 浮在页面上，但鼠标滚轮 / 触屏滑动仍会冒泡
    // 到 <body>，导致用户在购物车里滚动时外层页面也跟着滚——视觉上很糟糕。
    //
    // 旧实现：给 <html> 与 <body> 加 overflow:hidden。
    // 旧实现的副作用：CSS 规范要求 position:sticky 必须有"最近的可滚动祖先"作为
    //   scrollport，<html> 一旦 overflow:hidden 就不再是 scrollport，页面里所有
    //   依赖 <html> 滚动的 sticky 元素会瞬间降级为 position:relative，跳回文档
    //   流的自然位置——视觉上表现为"sidebar 打开瞬间 sticky 图片往上跳一段"。
    //
    // 新实现：在 mask 元素上监听 wheel / touchmove 事件并 preventDefault。
    //   - <html> 的 scrollport 始终保留 → sticky 永远有合法滚动祖先，不降级
    //   - mask 在 sidebar 打开时（CSS visibility:visible）盖满视口；触发的
    //     wheel/touchmove 事件被 preventDefault 吃掉 → 外层物理上无法滚动
    //   - mask 关闭时 visibility:hidden，按规范不接收 pointer-events，
    //     wheel/touchmove 监听自动哑火，无需手动 add/remove
    //   - sidebar 内部的事件目标在 .my-custom-cart-contwrap 上，不会冒泡到
    //     mask（两者是兄弟节点），sidebar 内部滚动不受影响
    //
    // 注意 passive:false：现代浏览器为性能默认把 wheel/touchmove 视为 passive，
    //   passive 监听里调 preventDefault 会被静默忽略；必须显式声明 passive:false
    //   才能真正阻止默认滚动行为。
    const _onMaskWheel = (e) => { e.preventDefault(); };
    const _onMaskTouchMove = (e) => { e.preventDefault(); };

    // ---------- 同步 Shopify 主题头部 cart icon 的角标数量 ----------
    // 主题头部的购物车图标内嵌一个 .icon-cart__total（由主题 Liquid 渲染，不在
    // 当前 Vue 应用接管的 #myCustomCartBundleId 范围内）。Shopify 原生并不会
    // 在 /cart/add|change|update|clear.js 调用后自动同步该元素的数字，因此需要
    // 我们在 cartData 变化时手动写 DOM。
    //
    // 实现要点：
    //   1) source 用 getter 精确订阅 item_count 字段，避免 cartData 整体替换
    //      但 item_count 未变时被无谓触发（例如改了优惠码、运费而数量没变）。
    //   2) cartData 初始可能为 null（极端情况下 Liquid 注入失败），用 ?? 0 兜底，
    //      不让 "undefined" / "null" 字面量出现在角标里。
    //   3) querySelectorAll：主题在 PC 头部 + 移动端抽屉各放一个图标，两处都要
    //      同步；找不到节点时 NodeList 为空，forEach 直接跳过，不报错。
    //   4) immediate:true：首屏 cartData 已由 window.myCustomCartInitialCart 注入，
    //      与主题 Liquid 渲染的角标值理论上一致；但若两者不一致（缓存 / 异步
    //      场景），首屏立刻用 Vue 这一份覆盖，避免短时不同步的视觉跳数。
    //   5) String(count) 显式转字符串，innerText 不接受 number 也会自动转，
    //      但显式更清楚，且对 0 不会因为 falsy 被忽略。
    watch(
      () => (cartData.value && cartData.value.item_count) ?? 0,
      (count) => {
        document.querySelectorAll('.icon-cart__total').forEach((el) => {
          el.innerText = String(count);
        });
      },
      { immediate: true }
    );


    console.log('==============================customCartCommonConfig', customCartCommonConfig.value)

    // ---------- 数据同步：重新拉取 /cart.js 覆盖 cartData ----------
    // 所有 cart API（add/change/update/clear）请求结束后都会调用此函数，
    // 把后端最新购物车状态同步到 ref，驱动模板里 items 列表 / 空状态切换。
    //
    // 失败处理：仅打日志、保留旧 cartData,不抛出 / 不弹错误,避免 sidebar 出现空白；
    // 用户至少还能看到上一次成功的状态,再次操作会再次触发刷新。
    //
    // 划线价 UX 优化：在 cartData.value = json 赋值前，先把"新加购商品"对应的
    //   products.js 全部 await 到位，避免新行先以"无划线"渲染、随后 flash 出划线价
    //   的视觉跳变（compare_at_price 来自 products.js 而非 cart.js，必须额外拉一次）。
    //   - 已缓存的产品：fetchProductInfo 立即 resolve，不阻塞
    //   - 网络失败 / 该变体无 compare_at_price：getOriginalLinePrice 自动退回 original_line_price，
    //     仅退化为"无划线"显示，不会卡住整个购物车更新
    //   - allSettled 而非 all：单个产品请求失败不影响其他，只要 cart.js 本身成功 cartData 就照常更新
    //   - 整体延迟 = max(本次新商品的 products.js 耗时)，已被全屏 isShowLoading 遮罩覆盖，
    //     用户感知上仍是"加购 → 购物车滑出 → 看到正确价格"的连续流程
    const refreshCart = async () => {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('cart.js status ' + res.status);
        const newCart = await res.json();

        // 在更新 cartData 前，先把新商品的 products.js 拉到位
        const items = (newCart && newCart.items) || [];
        const promises = items
          .filter(it => it.product_id != null && it.handle && !productInfoCache.value[it.product_id])
          .map(it => fetchProductInfo(it.handle, it.product_id));
        if (promises.length > 0) {
          await Promise.allSettled(promises);
        }

        cartData.value = newCart;
      } catch (err) {
        console.log('refreshCart error:', err);
      }
    };

    // ---------- 已加购商品的 products.js 缓存（用于补齐 compare_at_price） ----------
    // 背景：
    //   cart.js 的 line item 上没有 compare_at_price 字段，仅 products.js 才有。
    //   为了在已加购列表展示"商品自身 sale 价"对应的划线原价，需要按 line item 的
    //   handle 调 /products/{handle}.js 把整份产品信息拉回来，再按 variant_id 取该变体的
    //   compare_at_price。
    //
    // 缓存策略：
    //   - 按 product_id 维度缓存（不是 variant_id）：同一商品的所有 variant 共用一份 products.js
    //   - 一次会话内 compare_at_price 不会变（商家改价后用户刷新页面即重拉），不需主动失效
    //   - _inflightProductIds：防止同一 product 在拉取过程中被并发触发多次（例如同一商品的两个
    //     variant 同时入购物车，watch 在一次刷新里会对它们各调一次 fetchProductInfo）
    //
    // 响应式：用 ref + 整体替换的写法（{ ...prev, [pid]: data }），
    //         避免 Vue 对深层 mutate 不响应 → 模板里 getOriginalLinePrice 自动重算
    const productInfoCache = ref({});
    // 用 Map<productId, Promise> 而非 Set<productId> 跟踪 in-flight 请求：
    //   原因：refreshCart 在更新 cartData 前需要 await 新商品的 products.js 到位，
    //         才能让首次渲染就显示正确划线价（避免"先无划线、后 flash 出划线"的视觉跳变）。
    //         如果只用 Set 标记 id，重复调用 fetchProductInfo 时第二次会立刻 return，
    //         调用方拿不到指向"真实 fetch 完成"的 Promise，无法正确等待。
    //         改用 Map 后，重复调用直接返回首个调用的 Promise，所有调用方都能正确 await。
    const _inflightProductPromises = new Map();
    const fetchProductInfo = (handle, productId) => {
      // 已缓存 / 缺参数 → 立即 resolve，调用方无需等待
      if (productId == null || !handle) return Promise.resolve();
      if (productInfoCache.value[productId]) return Promise.resolve();
      // 已在飞 → 复用同一 Promise，避免并发重复请求 + 让所有调用方 await 到同一完成时刻
      if (_inflightProductPromises.has(productId)) return _inflightProductPromises.get(productId);

      const promise = (async () => {
        try {
          const res = await fetch(`/products/${handle}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error('products.js status ' + res.status);
          const json = await res.json();
          // 整体替换触发响应式；用 product_id 而非 handle 作 key 与 cart item 的 product_id 字段对齐
          productInfoCache.value = { ...productInfoCache.value, [productId]: json };
        } catch (err) {
          // 静默失败：getOriginalLinePrice 拿不到 cache 时自动退回 original_line_price，
          // UI 退回到"无 compare_at_price 划线"的现状，不影响其他功能
          console.log('fetchProductInfo error:', err);
        } finally {
          _inflightProductPromises.delete(productId);
        }
      })();

      _inflightProductPromises.set(productId, promise);
      return promise;
    };

    // 监听购物车里"出现过的 product_id 集合"变化，对未缓存的产品按需拉取
    //   - source 用 product_id 拼接的字符串，避免每次 cartData 整体替换都触发（例如改数量、改优惠码）
    //   - immediate:true：首屏 cart 快照已注入，立即给已加购的商品补一次 products.js
    //   - 注意：删除商品时 product_id 集合变小，此处不主动清缓存（同一商品下次再加购可直接命中）
    watch(
      () => {
        const items = (cartData.value && cartData.value.items) || [];
        return items.map(it => it.product_id).join('|');
      },
      () => {
        const items = (cartData.value && cartData.value.items) || [];
        items.forEach(it => fetchProductInfo(it.handle, it.product_id));
      },
      { immediate: true }
    );

    // ---------- 价格格式化：Shopify 价格 → 日文金额字符串 ----------
    // Shopify cart API 返回的 price/line_price/final_line_price 等字段统一为
    // "货币的最小单位 × 100"（即便 JPY 是零小数货币，price 也是 主单位×100）：
    //   例：商品标价 ¥14,980  →  接口返回 1498000
    // 显示思路：先除以 100 得到主单位整数，再用 ja-JP locale 加千位分隔，前缀 ¥
    //   1498000  →  14980  →  "14,980"  →  "¥14,980"
    //
    // 入参为 null/undefined 时返回空串，避免模板渲染出 "¥NaN"
    const formatPrice = (cents) => {
      if (cents == null) return '';
      const main = Math.round(cents / 100);
      return '¥' + main.toLocaleString('ja-JP');
    };

    // ---------- 获取"打折前的 line 总价" ----------
    // 业务规则（与产品需求对齐）：互斥取一种来源
    //   1) line 级有折扣（line_level_discount_allocations.length > 0）
    //      → 走"折扣码 / 自动折扣 / 脚本折扣"语义，划线价取 original_line_price
    //         （即 price * quantity，未叠加任何 line 级折扣的金额）
    //   2) line 级无折扣
    //      → 查 productInfoCache[product_id].variants[variant_id].compare_at_price
    //         compare_at_price > final_price 时，划线价取 compare_at_price * quantity
    //         （体现"商品自身 sale 价"对应的原价对比）
    //
    // 为何"互斥"而非"取两者最大值"：
    //   产品明确要求二选一，不希望折扣码与 compare_at_price 同时影响划线价的展示语义。
    //
    // 兜底链路：
    //   - cart.js 的 line_level_discount_allocations 在每条 line 上必然存在（即便为空数组），
    //     这里仍用 `(item.line_level_discount_allocations || [])` 兜一道，防极端缺字段
    //   - productInfoCache 还没拉到 / 拉失败 / 该变体无 compare_at_price / compare_at_price ≤ price：
    //     统一退回 original_line_price，hasDiscount 自然返回 false → 不显示划线，与改造前的
    //     默认行为保持一致，不会引入 UI 异常
    //
    // 注意 compare_at_price 单位：products.js 中 variant.compare_at_price 与 cart.js 的
    //   final_price 都是 cents（货币最小单位），可直接相乘相比，无需换算。
    const getOriginalLinePrice = (item) => {
      // 1) line 级有折扣 → 走 original_line_price（与之前行为一致）
      const lineAllocs = item.line_level_discount_allocations || [];
      if (lineAllocs.length > 0) {
        return item.original_line_price;
      }
      // 2) 无 line 级折扣 → 查 products.js 缓存里该变体的 compare_at_price
      const product = productInfoCache.value[item.product_id];
      if (product && Array.isArray(product.variants)) {
        // products.js 的 variant.id / cart item 的 variant_id 都是数字，但跨 API 来源仍 String() 比较稳妥
        const variant = product.variants.find(v => String(v.id) === String(item.variant_id));
        if (variant && variant.compare_at_price && variant.compare_at_price > item.final_price) {
          return variant.compare_at_price * item.quantity;
        }
      }
      // 3) 兜底：拉取尚未完成 / 该变体未配 compare_at_price → 退回 original_line_price
      return item.original_line_price;
    };

    // 是否存在折扣（满足任一打折来源即为 true）
    // 用于模板二选一渲染：true 走"红字 + 划线原价 + 优惠额 + 折扣码徽章"完整版；
    // false 仅展示一个 final 价（保持原状，不画红 + 划线 + 提示）
    const hasDiscount = (item) => {
      return getOriginalLinePrice(item) > item.final_line_price;
    };

    // ---------- 识别"赠品"行（BOGOS.io Free Gift） ----------
    // 业务背景：
    //   店铺接入了 BOGOS.io 这款"自动赠品"App，加购指定主商品时，App 会通过
    //   后端规则把赠品行直接塞进 cart（不需要前端主动加购）；
    //   赠品行同样会出现在 /cart.js 的 items 列表里，但有以下特征：
    //     1) 单价被 BOGOS.io 用 100% 折扣拨付到 0 → final_price === 0
    //     2) product_description 含 "BOGOS.io Free Gift" 标识（App 在产品描述里写入的标签）
    //   赠品的"删除"由 BOGOS.io 自身联动控制：用户删除主商品时，赠品会一起被服务端清理；
    //   因此本前端不应让用户单独删除 / 改数量赠品行，否则会与 App 的状态发生冲突。
    //
    // 判定为何"两个条件同时满足"：
    //   - 仅看 final_price === 0：可能因配置错误 / 其他活动让付费商品价格归零，会误判
    //   - 仅看 product_description：无法保证 App 当前确实把它当做赠品（边界场景下
    //     用户可能以付费方式购买同一商品，此时 final_price > 0 不应视为赠品）
    //   两者同时成立才是"BOGOS.io 当下作为赠品拨付"的可靠组合
    //
    // 字段安全性：
    //   - product_description 在某些 line item 上可能缺失（cart.js 不保证），
    //     用 `|| ''` 兜底再 includes，避免对 undefined 调 includes 报错
    //   - final_price 通常存在；不存在时 `=== 0` 会因 undefined !== 0 自然返回 false，
    //     不会把缺字段的行误判为赠品
    const isGiftItem = (item) => {
      if (!item) return false;
      if (item.final_price !== 0) return false;
      const desc = item.product_description || '';
      return desc.includes('BOGOS.io Free Gift');
    };

    // ---------- 行级数量错误提示（库存不足等） ----------
    // 结构：{ [item.key]: '当前商品の在庫が不足しています...' }
    // 与 pending 同样"新对象赋值"以触发响应式；4 秒后自动清除避免文案常驻
    const qtyErrors = ref({});
    const _qtyErrorTimers = {};
    const setQtyError = (key, msg) => {
      qtyErrors.value = { ...qtyErrors.value, [key]: msg };
      clearTimeout(_qtyErrorTimers[key]);
      _qtyErrorTimers[key] = setTimeout(() => clearQtyError(key), 4000);
    };
    const clearQtyError = (key) => {
      if (qtyErrors.value[key] != null) {
        const next = { ...qtyErrors.value };
        delete next[key];
        qtyErrors.value = next;
      }
      clearTimeout(_qtyErrorTimers[key]);
      _qtyErrorTimers[key] = null;
    };

    // ---------- 数量更新：统一入口（按钮 / 输入框都走这里） ----------
    // 参数：
    //   item    当前 line（必须取 item.key 作为 id；同 variant 不同属性会被拆成多行）
    //   rawQty  目标绝对数量（来自 +/- 计算 或 input 输入）
    //
    // 并发保护：用全局 isShowLoading 作为锁，配合模板里的全屏遮罩拦截
    //   所有用户操作（不再单独把当前行的按钮 / 输入框置灰）。
    //   这样视觉上保持原色（按设计稿要求），但用户实际无法触发并发请求。
    //
    // 库存策略：
    //   - cart.js 不带 inventory_quantity，前端无法预判库存上限
    //   - 依赖 Shopify 服务端：当 inventory_policy=deny 且超量时返回 HTTP 422
    //     body 为 { status, message, description }，description 是面向用户的文案
    //     例 "You can only add 5 of SwitchBot ハブ3 to your cart."
    //   - 拿到 description 后通过 setQtyError 显示在该行下方，4s 后自动消失
    //   - 错误时再 refreshCart 一次，确保 UI 与服务端一致（可能 cap 后数量与请求不同）
    //
    // 成功时直接用响应体覆盖 cartData，让 UI 立刻反映新数量（绕过 hijack 的 200ms 防抖）
    const applyItemQuantity = async (item, rawQty) => {
      // 赠品（BOGOS.io Free Gift）禁止修改数量：模板已隐藏 +/- 与输入框，
      // 这里再兜一道防御（防止外部代码 / devtools 直接调用本方法绕过 UI）
      if (isGiftItem(item)) return;
      // 转 number → 取整 → 下限 1（避免 0 / 负数误删，删除走 trash 按钮）
      const newQty = Math.max(1, Math.floor(Number(rawQty) || 0));
      if (newQty === item.quantity) return;
      // 全局锁：已有请求在飞时直接放弃本次（理论上遮罩已挡住点击，这里再兜一道）
      if (isShowLoading.value) return;
      isShowLoading.value = true;
      clearQtyError(item.key);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ id: item.key, quantity: newQty }),
        });
        const json = await res.json();
        if (!res.ok) {
          // 422 等错误：description 是 Shopify 准备的用户文案，优先用
          setQtyError(item.key, json.description || json.message || '数量を更新できませんでした');
          // 拉一次最新 cart 同步，避免 UI 与真实服务端状态偏离
          await refreshCart();
          return;
        }
        // 成功：响应体即更新后的整个 cart，直接同步
        cartData.value = json;
      } catch (err) {
        console.log('applyItemQuantity error:', err);
        setQtyError(item.key, '通信エラーが発生しました');
      } finally {
        isShowLoading.value = false;
      }
    };

    // +/- 按钮：在当前数量基础上 ±1，转换为绝对值后调 applyItemQuantity
    const changeItemQuantity = (item, delta) => applyItemQuantity(item, item.quantity + delta);

    // ---------- 删除单行商品 ----------
    // 点击 trash 按钮直接调用，不做二次确认（产品需求）。
    // 实现要点：
    //   1) 复用与 applyItemQuantity 相同的全局锁 isShowLoading：API 飞行期间
    //      整车遮罩拦截所有交互，避免连点 / 并发删除
    //   2) 通过 /cart/change.js + quantity:0 删除 line（Shopify 标准做法，
    //      与 /cart/clear.js 不同——后者会清空整车）
    //   3) 成功响应体即更新后的整车，直接覆盖 cartData，UI 立即同步无需再拉
    //      一次 /cart.js（绕开 hijack 的 200ms 防抖延迟）
    //   4) 失败时把服务端 description 显示在该行下方（与库存超量复用同一套
    //      错误展示机制），并 refreshCart 一次确保 UI 与真实状态一致
    const removeItem = async (item) => {
      // 赠品行禁止单独删除：BOGOS.io 联动机制要求赠品必须随主商品一起清理，
      // 用户单删赠品会让前端 UI 与 App 服务端状态不一致。模板已隐藏 trash 按钮，
      // 这里再兜一道防御（防止外部代码 / devtools 直接调用本方法绕过 UI）
      if (isGiftItem(item)) return;
      // 全局锁：已有请求在飞时直接放弃（理论上遮罩已挡住点击，这里再兜一道）
      if (isShowLoading.value) return;
      isShowLoading.value = true;
      clearQtyError(item.key);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          // quantity:0 是 Shopify 删除 line 的官方姿势
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
        const json = await res.json();
        if (!res.ok) {
          setQtyError(item.key, json.description || json.message || '商品を削除できませんでした');
          await refreshCart();
          return;
        }
        // 成功：响应体已是删除后的整车，直接同步
        cartData.value = json;
      } catch (err) {
        console.log('removeItem error:', err);
        setQtyError(item.key, '通信エラーが発生しました');
      } finally {
        isShowLoading.value = false;
      }
    };

    // 输入框 blur：读取 input 值 → applyItemQuantity → 把 input 同步到真实数量
    // 为什么最后要手动同步：Vue :value 一致时不会触发 DOM patch，
    //   例 用户输入 "999"，库存 cap 到 5，cartData.quantity 仍可能是 5（未变），
    //   :value="5" → "5" Vue 认为无变化，不会把 DOM 输入框拉回，造成显示与真实值偏离。
    const onQtyInputBlur = async (item, e) => {
      const target = e.target;
      const raw = parseInt(target.value, 10);
      // 非法输入（空 / 非数字）直接拉回真实数量
      if (isNaN(raw)) {
        target.value = item.quantity;
        return;
      }
      await applyItemQuantity(item, raw);
      // 取最新 cartData 中该行的 quantity 同步回 input
      // 注：item 是 v-for 的旧引用，cartData 已被 applyItemQuantity 替换，需按 key 重新查找
      const fresh = cartData.value && cartData.value.items
        ? cartData.value.items.find(it => it.key === item.key)
        : null;
      if (fresh) target.value = fresh.quantity;
      else target.value = item.quantity; // 极端情况下（行已被删除）兜底
    };

    // 加购"请求发出"的瞬间立刻打开 sidebar + 显示 loading
    // 不等响应，给用户最快的视觉反馈；响应回来后由 handleUpdate 关闭 loading
    const handleAddStart = () => {
      isShowCart.value = true;
      isShowLoading.value = true;
    };
    // 接口响应事件：所有 cart API 调用结束后统一刷新数据 + 关闭 loading
    //   - 不区分 type：add 会改 items；change/update 改数量；clear 清空——都需要重拉
    //   - loading 始终设回 false：仅 add 会开启 loading，但即便其他类型也设 false，
    //     可兜底处理"上一次 add 仍在 loading 时又触发其他操作"的边界情况
    const handleUpdate = async (event) => {
      await refreshCart();
      isShowLoading.value = false;
    };

    // ============================================================================
    // 底部结算栏：折扣分类 + 优惠码应用/取消 + 结账跳转
    // ----------------------------------------------------------------------------
    // Shopify 把"折扣"分散在两个地方：
    //   - cart.discount_applications        所有折扣的元数据（title / type / target_*）
    //   - item.line_level_discount_allocations  每行 line 上"实际拨付"的折扣金额
    //
    // 为了实现设计稿的"产品折扣 / 订单折扣"两行分开展示，需按以下规则切分：
    //   产品折扣（製品のディスカウント）
    //     来源：item.line_level_discount_allocations
    //     过滤：discount_application.target_selection !== 'all'
    //           （即仅"作用于特定商品"的折扣，例如 entitled / explicit）
    //     合并：同一 application 可能拨付到多行 line，按 (type+title) 合并展示一个 tag
    //     总额：累加各 line 的 alloc.amount（已经是该折扣实际作用到 line 的金额）
    //
    //   订单折扣（注文のディスカウント）
    //     来源：cart.cart_level_discount_applications（Ajax /cart.js 的字段名，
    //          注意不是 Liquid 端 cart 对象的 discount_applications，那个 Ajax 拿不到）
    //     范围：仅整车级折扣（target_selection === 'all' 的折扣应用）
    //     总额：累加 app.total_allocated_amount（cart 级总额）
    //
    //   运费折扣（不展示）
    //     /cart.js 不返回运费折扣，因为运费要到 checkout 阶段才能算出来
    //     (按地址/物流商)。sidebar 阶段无法预知运费优惠，只能在结账页展示。
    //
    // 折扣是否可被用户主动取消（折扣 tag 右上角的 ×）：
    //   - app.type === 'discount_code'  用户输入的折扣码 → 可取消
    //   - 其他（automatic/manual/script）→ 不可取消，× 不渲染
    // ============================================================================

    // 优惠码输入框 v-model
    const couponCode = ref('');

    // 产品折扣列表（line 级、非整车）
    const productDiscounts = computed(() => {
      // 没有 cart 或 items 时返回空数组，模板的 v-if 会隐藏整行
      if (!cartData.value || !cartData.value.items) return [];
      // 用 Map 按"折扣应用"维度合并：同一折扣命中多行 line 时只展示一个 tag
      const map = new Map();
      cartData.value.items.forEach(item => {
        (item.line_level_discount_allocations || []).forEach(alloc => {
          const app = alloc.discount_application || {};
          // 跳过整车折扣（归到订单折扣那一行处理，这里只保留产品级）
          if (app.target_selection === 'all') return;
          // 合并 key：用 type+title 作为唯一标识，足以区分不同折扣
          const key = (app.type || '') + '::' + (app.title || '');
          if (!map.has(key)) {
            map.set(key, {
              key,
              title: app.title || '',
              // 仅 discount_code 可被用户取消，其余类型不显示 ×
              removable: app.type === 'discount_code',
              // discount_code 类型的 title 通常即为折扣码本身（Shopify 默认行为），
              // 取消接口需要这个 code 值
              code: app.type === 'discount_code' ? (app.title || '') : '',
              amount: 0,
            });
          }
          // 同一折扣在多行的拨付金额累加
          map.get(key).amount += alloc.amount || 0;
        });
      });
      // 排序：手动添加的折扣码在前，自动 / 内置类折扣在后。
      // - removable 在本计算里 等价于 "是 discount_code 类型"，直接当排序键复用
      // - JS 的 Array.sort 自 ES2019 起保证稳定，相同优先级的 tag 维持插入顺序
      return Array.from(map.values()).sort(
        (a, b) => (a.removable ? 0 : 1) - (b.removable ? 0 : 1)
      );
    });

    // 产品折扣总额：仅累加产品级，作为该行最右的 - ¥XXX
    const productDiscountTotal = computed(() => {
      return productDiscounts.value.reduce((sum, d) => sum + d.amount, 0);
    });

    // 订单折扣列表（整车级）
    // ----------------------------------------------------------------------------
    // 数据源说明：
    //   /cart.js 响应中 cart 级折扣字段是 cart_level_discount_applications，
    //   而不是 Liquid 端 cart 对象的 discount_applications（那是 Storefront/Liquid 概念，
    //   Ajax API 中不存在，写错会拿到 undefined 导致整行不渲染——历史 bug 已修正）。
    //
    // 关于运费折扣（target_type=shipping_line）：
    //   Shopify 的 Ajax cart.js 不返回运费折扣，因为运费要到 checkout 阶段
    //   才会按地址 / 物流商计算出来。所以 sidebar 里没法展示运费折扣，
    //   仅在结账页可见。这里只展示 cart 级订单折扣。
    const orderDiscounts = computed(() => {
      if (!cartData.value) return [];
      const apps = cartData.value.cart_level_discount_applications || [];
      return apps
        .map((app, idx) => ({
          // idx 拼进 key 防止同名折扣并存时 key 冲突
          key: 'order-' + idx + '-' + (app.title || ''),
          title: app.title || '',
          removable: app.type === 'discount_code',
          code: app.type === 'discount_code' ? (app.title || '') : '',
          // cart 级折扣金额：直接读 total_allocated_amount，无需自行汇总
          amount: app.total_allocated_amount || 0,
        }))
        // 排序规则同 productDiscounts：手动折扣码在前，自动/内置在后
        .sort((a, b) => (a.removable ? 0 : 1) - (b.removable ? 0 : 1));
    });

    // 订单折扣总额：仅累加订单+运费类折扣
    const orderDiscountTotal = computed(() => {
      return orderDiscounts.value.reduce((sum, d) => sum + d.amount, 0);
    });

    // ---------- 应用优惠码 ----------
    // 实现思路：
    //   /cart/update.js 的 discount 字段是"覆盖式"——传入的字符串会替换当前所有折扣码。
    //   因此要把"已存在的 codes"+"新输入的 code"合并成 CSV 一次性提交，
    //   才能在保留原 codes 的前提下追加新 code。
    // 失败处理：
    //   - 输入为空直接返回（不报错）
    //   - 应用失败时不抛出，refreshCart 保证 UI 与服务端不偏离
    //   - 全局 isShowLoading 锁防止并发提交
    const applyCouponFn = async () => {
      const code = couponCode.value.trim();
      if (!code) return;
      if (isShowLoading.value) return;
      isShowLoading.value = true;
      try {
        // 收集已应用的折扣码
        const existing = (cartData.value && cartData.value.discount_codes
          ? cartData.value.discount_codes.map(d => d.code)
          : []);
        // 去重追加：避免重复输入同一个 code 时变成 "ABC,ABC"
        if (!existing.includes(code)) existing.push(code);
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ discount: existing.join(',') }),
        });
        const json = await res.json();
        if (res.ok) {
          // 成功：响应体即更新后的整车，直接同步并清空输入框
          cartData.value = json;
          couponCode.value = '';
        } else {
          // 失败：拉一次最新 cart，避免 UI 与服务端偏离
          // （不展示错误文案，保持简洁；后续如需提示可在此加 toast）
          await refreshCart();
        }
      } catch (err) {
        console.log('applyCouponFn error:', err);
      } finally {
        isShowLoading.value = false;
      }
    };

    // ---------- 取消优惠码（× 按钮） ----------
    // 实现思路同上：把 cart.discount_codes 中除目标 code 外的全部重新 POST，
    // 利用 /cart/update.js 的覆盖语义实现"删除一个"的效果。
    // 仅在 discount_code 类型的 tag 上渲染按钮，所以不需要在这里二次校验类型。
    const removeDiscountFn = async (code) => {
      if (!code) return;
      if (isShowLoading.value) return;
      isShowLoading.value = true;
      try {
        // 过滤出"剩余"的 codes，重新整体提交
        const remaining = (cartData.value && cartData.value.discount_codes
          ? cartData.value.discount_codes
              .map(d => d.code)
              .filter(c => c !== code)
          : []).join(',');
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          // remaining 为空字符串时表示清空所有折扣码（Shopify 标准用法）
          body: JSON.stringify({ discount: remaining }),
        });
        const json = await res.json();
        if (res.ok) {
          cartData.value = json;
        } else {
          await refreshCart();
        }
      } catch (err) {
        console.log('removeDiscountFn error:', err);
      } finally {
        isShowLoading.value = false;
      }
    };

    // ---------- 跳转结账 ----------
    // 直接跳 Shopify 标准结账 URL，由 Shopify 自身处理 cart→checkout 转交
    const goCheckoutFn = () => {
      window.location.href = '/checkout';
    };

    // ============================================================================
    // 折扣进度条（progress bar）
    // ----------------------------------------------------------------------------
    // 数据来源：window.customCartCommonConfig.progressConfig（数组）
    //   每个元素：{
    //     type:    "quantity_discount" | "price_discount" | "shipping",
    //     enabled: boolean,                        // 仅取第一个 enabled=true 的项
    //     finalTitle:   string,                         // 全部达成时的兜底文案
    //     items:   [{ label, target }, ...]        // 阶梯目标（不要求按 target 升序）
    //   }
    //
    // 三种类型对应"购物车的当前值"：
    //   quantity_discount → cart.item_count   (购物车数量满 X 即得 Y 优惠)
    //   price_discount    → cart.total_price  (折后金额满 X 即得 Y 优惠)
    //   shipping          → cart.total_price  (折后金额满 X 即免运费)
    //
    // 注：Shopify cart.js 中 total_price 是"已扣全部折扣"的应付金额，且单位为
    //     "主货币 × 100"（例 14,980 円 → 1498000）。target 在配置中应直接填日元主单位
    //     （例 5000 表示 5000 円），所以比较前需把 cart.total_price / 100 转回主单位。
    //
    // 引导文案模板：
    //   有未达成目标 → "あと{差额}{单位}で、{下一档label}適用"
    //     - quantity 单位为 "点"
    //     - price / shipping 单位用 formatPrice 封装的"¥1,234"
    //   全部达成    → 兜底用 activeProgress.title；title 为空时给固定占位 "全ての特典を適用済み"
    // ============================================================================

    // 首个 enabled=true 的进度条配置；找不到时为 null（模板整块 v-if 隐藏）
    const activeProgress = computed(() => {
      const list = (customCartCommonConfig.value && customCartCommonConfig.value.progressConfig) || [];
      // Array.find 命中即返回，未命中返回 undefined → 用 || null 归一化
      return list.find(p => p && p.enabled) || null;
    });

    // 进度条对应的"购物车当前值"（数量 or 商品展示价格之和-主单位）
    // 没有命中的进度条 / 无 cart 数据时返回 0；用作 progressPercent 的分子
    const currentProgressValue = computed(() => {
      if (!activeProgress.value || !cartData.value) return 0;
      const type = activeProgress.value.type;
      if (type === 'quantity_discount') {
        return cartData.value.item_count || 0;
      }
      // price_discount / shipping：累加每行 final_line_price（商品展示价格）
      // final_line_price 仅受商品自身折扣（compare_at_price）和 line 级折扣码影响，
      // 不受订单级整车折扣影响——与 DOM 中 .my-custom-cart-item-price-current 展示的值一致
      const items = cartData.value.items || [];
      const sum = items.reduce((acc, item) => acc + (item.final_line_price || 0), 0);
      return Math.floor(sum / 100);
    });

    // 排序后的 items（按 target 升序），便于查找"下一档未达成"
    // 用 [...arr].sort() 拷贝再排，避免修改原配置数组
    const activeProgressItems = computed(() => {
      if (!activeProgress.value) return [];
      const items = activeProgress.value.items || [];
      return [...items].sort((a, b) => (a.target || 0) - (b.target || 0));
    });

    // 进度条的 100% 锚点：取最高那档 target 作为最大刻度
    // items 为空 / 全部 target=0 时返回 0，progressPercent 内部会再兜底为 0
    const progressMaxTarget = computed(() => {
      const items = activeProgressItems.value;
      if (!items.length) return 0;
      return items[items.length - 1].target || 0;
    });

    // 当前进度百分比（0-100），驱动 fill 宽度
    // - 上限 100：超过最高档时填满，不允许溢出 100%
    // - 下限 0：cart 为空 / max=0 时也保持 0%
    const progressPercent = computed(() => {
      const max = progressMaxTarget.value;
      if (!max) return 0;
      const cur = currentProgressValue.value;
      const pct = (cur / max) * 100;
      return Math.min(100, Math.max(0, pct));
    });

    // 单个节点是否已达成（决定节点 reached 类）
    const isProgressNodeReached = (item) => {
      return currentProgressValue.value >= (item.target || 0);
    };

    // 单个节点在进度条上的水平位置百分比
    // 与 progressPercent 同一锚点，确保 fill 边沿与对应节点视觉吻合
    const getProgressNodePosition = (item) => {
      const max = progressMaxTarget.value;
      if (!max) return 0;
      const target = item.target || 0;
      return Math.min(100, Math.max(0, (target / max) * 100));
    };

    // 引导文案：根据类型 + 下一档未达成 item 拼出
    // 全部达成时退化为 finalTitle，finalTitle 也为空则给固定占位
    const progressTip = computed(() => {
      const cfg = activeProgress.value;
      if (!cfg) return '';
      const items = activeProgressItems.value;
      const cur = currentProgressValue.value;
      // 找第一个未达成的目标
      const nextItem = items.find(i => cur < (i.target || 0));
      if (!nextItem) {
        // 全部达成：优先用配置的 finalTitle；缺省给一个通用日文兜底
        return cfg.finalTitle || '全ての特典を適用済み';
      }
      const diff = (nextItem.target || 0) - cur;
      const label = nextItem.label || '';
      if (cfg.type === 'quantity_discount') {
        // 数量类：单位 "点"
        return `あと${diff}点で、${label}適用`;
      }
      // 金额类（price_discount / shipping）：用 formatPrice 输出 "¥1,234"
      // formatPrice 接收 cents，所以这里把主单位 diff 反推为 cents（× 100）传入
      return `あと${formatPrice(diff * 100)}で、${label}適用`;
    });

    // ============================================================================
    // 购物车空状态推荐列表（"おすすめIoT製品"）
    // ----------------------------------------------------------------------------
    // 数据来源：customCartCommonConfig.defaultRecommendList（数组）
    //   元素结构为 Shopify 产品对象（来自 /products.json 或 /products/{handle}.js）
    //   variants[] 每项关键字段：id / title / price / compare_at_price / available / featured_image
    //
    // 显示条件：购物车空 + 配置数组非空时整块渲染（showDefaultRecommend）
    //
    // 价格单位适配：
    //   /products.json     变体 price 为字符串主单位（例 "14980.00"）
    //   /products/{handle}.js / /cart.js 是整数 cents（例 1498000，即 14980 円 × 100）
    //   formatRecomPrice 内部按"是否含小数点"自动二选一处理
    //
    // 加购流程：
    //   addRecomProdToCart 调 fetch('/cart/add.js') →
    //     fetch 劫持器（my-custom-cart.js 顶部 myCustomCartListenEvent）捕获 →
    //     派发 myCusCart:AddStart 事件（handleAddStart 打开 sidebar + loading） →
    //     响应返回后派发 myCusCart:Update（handleUpdate 刷新 cart + 关 loading）
    //   全程不需要本函数手工 refreshCart，sidebar 自动转入"已加购列表"展示
    // ============================================================================

    // 推荐产品数组（购物车空时使用）：从 customCartCommonConfig 取，无配置时为空数组
    const defaultRecommendList = computed(() => {
      const list = customCartCommonConfig.value && customCartCommonConfig.value.defaultRecommendList;
      return Array.isArray(list) ? list : [];
    });

    // 整块显隐：购物车为空 + 默认推荐列表非空 才显示
    // 仍保留此 computed 单独使用，因为模板里"空状态文案"需要据此切到 has-recom 类调整布局，
    // 与"购物车非空时的 API 推荐"互不影响（has-recom 仅作用于空状态文案）
    const showDefaultRecommend = computed(() => {
      const isEmpty = !cartData.value
        || !cartData.value.items
        || cartData.value.items.length === 0;
      return isEmpty && defaultRecommendList.value.length > 0;
    });

    // ============================================================================
    // 购物车非空时：基于"购物车第一件付费商品（非赠品）"调用 Shopify 推荐 API 获取推荐
    // ----------------------------------------------------------------------------
    // 接口：GET /recommendations/products.json?product_id={pid}&limit=5&intent=related
    //   - product_id：以"购物车第一件付费商品（非赠品）"的 product_id 作为锚点
    //     之所以要跳过赠品：用户加购某个商品后，自动赠品规则可能把赠品塞到 items[0]，
    //     若直接用 items[0] 锚点，推荐结果会围绕"赠品"而非"用户真正购买的商品"展开，
    //     与购买意图错位。判定方式：line item 的 final_price === 0
    //       - final_price 是"扣完所有 line 级折扣后的单价 cents"
    //       - 自动赠品规则以 100% 折扣形式拨付到 line，使 final_price 归零
    //       - 普通付费商品 final_price 始终 > 0（即便有打折也不会到 0）
    //     极端兜底：所有商品 final_price 都为 0（配置错误等）时仍取 items[0]，
    //     保证至少有一个锚点，不让推荐区整块消失
    //   - limit=5    ：最多返回 5 个推荐（不足则返回更少；不会强行填满）
    //   - intent=related：默认意图，"相关 / 你可能也喜欢"风格的推荐
    // 响应结构：{ products: [ <Shopify Product>, ... ] }
    //   每个 product 字段与 /products/{handle}.js / /products.json 一致，
    //   现有 getRecomProdImage / getRecomProdTitle / formatRecomPrice 等
    //   helpers 完全兼容，无需为此再写一套渲染逻辑。
    //
    // 触发时机：watch 购物车"第一件付费商品的 product_id"
    //   - 加购第一件付费商品（空 → 非空）：firstProductId 从 null 变为数字 → 拉取
    //   - 删除该付费商品后下一件付费商品顶上：firstProductId 切换 → 重新拉取
    //   - 改数量 / 加购重复商品 / 仅赠品变化：firstProductId 不变 → 不重复请求（避免抖动）
    //   - 清空购物车（非空 → 空）：firstProductId → null → 清空 apiRecommendList，
    //     模板会自动回落展示 defaultRecommendList（配置文档来源）
    // ============================================================================

    // 来自推荐 API 的产品列表（购物车非空时使用）
    const apiRecommendList = ref([]);

    // 拉取 Shopify 推荐 API；失败 / 无 productId 时清空，由 currentRecommendList 兜底空数组
    const fetchApiRecommendations = async (productId) => {
      if (!productId) {
        apiRecommendList.value = [];
        return;
      }
      try {
        // limit=5 为业务约定上限；intent=related 为"相关推荐"标准用法
        const url = `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=5&intent=related`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('recommendations api status ' + res.status);
        const json = await res.json();
        // 接口偶尔可能返回非数组（异常 / 边界情况），统一兜底成 []
        apiRecommendList.value = Array.isArray(json.products) ? json.products : [];
        // 特殊处理的函数
        apiRecommendList.value = specialHandleRecomList(productId, apiRecommendList.value);
      } catch (err) {
        // 静默失败：保留空数组，模板自动隐藏整块；不抛错避免污染外层链路
        console.log('fetchApiRecommendations error:', err);
        apiRecommendList.value = [];
      }
    };
    const specialHandleRecomList = (productId, list) => {
      if (productId + '' !== '8600795447471') return list;
      if (!Array.isArray(list)) return [];
      return list.filter(item => item && item.id + '' !== '7436950896815');
    };

    // 监听"购物车第一件付费商品（非赠品）的 product_id"，变化时重新拉取推荐
    //   - source 写成 getter，依赖 cartData.value.items 的响应式追踪
    //   - 跳过赠品：line item 的 final_price === 0 视为赠品，逐项 find 命中第一个非 0 的；
    //     全部为 0（极端配置错误场景）时兜底用 items[0]，保证推荐区不整块消失
    //   - 空购物车返回 null（不是 undefined），保证响应式比较语义清晰
    //   - immediate:true：首屏 cart 快照若已有商品，立即拉一次推荐（避免首次打开 sidebar
    //     还要等用户操作才出现推荐）
    watch(
      () => {
        const items = cartData.value && cartData.value.items;
        if (!items || items.length === 0) return null;
        // 第一个 final_price > 0 的商品即"用户真正付费购买的商品"；
        // 找不到（全是赠品）时退回 items[0]，避免锚点丢失推荐区消失
        const firstPaid = items.find(it => (it.final_price || 0) > 0);
        return (firstPaid || items[0]).product_id;
      },
      (firstProductId) => {
        fetchApiRecommendations(firstProductId);
      },
      { immediate: true }
    );

    // 同 defaultRecommendList 的处理：列表更新后，给每个产品填一个默认选中变体
    // - immediate:true：apiRecommendList 初始为 []，首次执行无副作用，但保持代码对称
    // - 不会覆盖已有选中：仅当某 prod.id 还没记录时才填默认值（用户手动切过的不被重置）
    // - 注：实际 watch 调用挪到下方 recomSelectedVariantIds 声明之后（避免 TDZ）
    //   这里仅作为说明文档，真正注册见下面"列表变化时初始化每个产品的默认选中变体（API 来源）"

    // ----------------------------------------------------------------------------
    // 当前展示的推荐列表（统一渲染入口）
    //   购物车空 → 取配置文档中的 defaultRecommendList（保持原有行为）
    //   购物车非空 → 取 API 拉到的 apiRecommendList（新增行为）
    // 模板里 v-for 直接消费此 computed，不需要在 v-if 上分两段写
    // ----------------------------------------------------------------------------
    const currentRecommendList = computed(() => {
      const isEmpty = !cartData.value
        || !cartData.value.items
        || cartData.value.items.length === 0;
      return isEmpty ? defaultRecommendList.value : apiRecommendList.value;
    });

    // 推荐区整块显隐：当前列表非空就渲染（不再判断购物车是否为空，由 currentRecommendList 内部决定取哪份数据）
    const showRecommend = computed(() => currentRecommendList.value.length > 0);

    // 选中变体 id 表：{ [prod.id]: variant.id }
    // - 用对象表保存，便于多个推荐产品并存时各自独立选中
    // - 浏览器 select 的 value 永远是 string，因此存进来的 id 也按 string 处理
    //   （查询时通过 String() 比较，避免 number/string 不等）
    const recomSelectedVariantIds = ref({});

    // 列表变化时初始化每个产品的默认选中变体（首个 variant）
    // - immediate 必开：首次渲染（recommendList 已经从 metafield 拿到）也要走一遍
    // - 不会覆盖已有选中：仅当某 prod.id 还没记录时才填默认值
    watch(defaultRecommendList, (list) => {
      const next = { ...recomSelectedVariantIds.value };
      let changed = false;
      list.forEach(prod => {
        if (next[prod.id] == null && prod.variants && prod.variants[0]) {
          next[prod.id] = prod.variants[0].id;
          changed = true;
        }
      });
      if (changed) recomSelectedVariantIds.value = next;
    }, { immediate: true });

    // 列表变化时初始化每个产品的默认选中变体（API 来源）
    // - 与上面 defaultRecommendList 的 watch 同结构：仅当某 prod.id 还没记录时才填默认值
    // - 放在这里而非 apiRecommendList 声明附近，是为了避免 TDZ：apiRecommendList 声明早于
    //   recomSelectedVariantIds，若那里直接 watch + immediate 会读到尚未初始化的引用
    // - immediate:true 与 defaultRecommendList 的 watch 对称；apiRecommendList 初始为 []，
    //   首次回调内部 forEach 不会执行任何赋值，无副作用
    watch(apiRecommendList, (list) => {
      const next = { ...recomSelectedVariantIds.value };
      let changed = false;
      list.forEach(prod => {
        if (next[prod.id] == null && prod.variants && prod.variants[0]) {
          next[prod.id] = prod.variants[0].id;
          changed = true;
        }
      });
      if (changed) recomSelectedVariantIds.value = next;
    }, { immediate: true });

    // ---------- 推荐变体是否在售（兼容 storefront 与 Admin REST 两种数据格式） ----------
    // 数据源差异：
    //   storefront 格式（API 推荐 /recommendations/products.json、/cart.js、/products/{handle}.js）
    //     变体上直接挂 available 布尔字段，true=有货 / false=缺货
    //   Admin REST 格式（metafield custom_cart_common_config.defaultRecommendList 当前使用）
    //     变体上没有 available 字段，需要按 inventory_management / inventory_policy /
    //     inventory_quantity 三件套自行推算，与 Shopify 自身计算 storefront available 同口径
    //
    // 推算规则（与 Shopify 计算 available 的官方口径对齐）：
    //   1) variant.available 是布尔 → 直接用（storefront 来源）
    //   2) inventory_management 为 null / 空 → 不追踪库存，永远视为有货
    //   3) inventory_policy === 'continue' → 允许超卖，即使 quantity<=0 也视为有货
    //   4) 其余情况（即默认 inventory_policy === 'deny'）→ 仅 inventory_quantity > 0 才视为有货
    //
    // 历史 bug：
    //   metafield 改成 Admin REST 格式后，变体没有 available 字段，模板里
    //   :disabled="!variant.available" 读到 undefined → !undefined === true →
    //   "カートに追加" 按钮永远禁用（即便库存 977 也点不动）。
    //   引入本 helper 后，模板用 isVariantAvailable(variant) 替代 variant.available，
    //   两种格式都能得出正确的可售状态。
    //
    // 注意：Number(variant.inventory_quantity) 容错——某些边界数据可能把库存写成字符串，
    //   先 Number 转一道再 || 0 兜底，避免 NaN > 0 永远 false。
    const isVariantAvailable = (variant) => {
      if (!variant) return false;
      if (typeof variant.available === 'boolean') return variant.available;
      if (!variant.inventory_management) return true;
      if (variant.inventory_policy === 'continue') return true;
      return (Number(variant.inventory_quantity) || 0) > 0;
    };

    // 是否需要显示变体下拉
    //   - 没 variants → 不显示
    //   - 仅 1 个变体且 title === 'Default Title'（无规格商品）→ 不显示
    //     避免出现"只有一个 Default Title 选项"的无意义下拉
    //   - 其他情况 → 显示
    const shouldShowVariantSelect = (prod) => {
      if (!prod.variants || prod.variants.length === 0) return false;
      if (prod.variants.length === 1 && prod.variants[0].title === 'Default Title') return false;
      return true;
    };

    // 取当前选中的变体对象；找不到时回落到第一个（兜底，正常不应触发）
    // - String() 比较：因为 select.value 是 string，variant.id 可能 number
    const getRecomSelectedVariant = (prod) => {
      if (!prod.variants || !prod.variants.length) return {};
      const id = recomSelectedVariantIds.value[prod.id];
      return prod.variants.find(v => String(v.id) === String(id)) || prod.variants[0];
    };

    // select @change：把新选中的 variant id 写入表
    // - 使用"新对象赋值"方式触发响应式更新，避免直接 mutate 原对象
    const onRecomVariantChange = (prod, e) => {
      recomSelectedVariantIds.value = {
        ...recomSelectedVariantIds.value,
        [prod.id]: e.target.value,
      };
    };

    // ---------- 推荐产品：详情页 URL ----------
    // 产品详情页 URL：优先用 prod.url（部分 API 返回），否则按 handle 拼，再不济用 #
    const getRecomProdUrl = (prod) => {
      if (prod.url) return prod.url;
      if (prod.handle) return '/products/' + prod.handle;
      return '#';
    };

    // ---------- 推荐产品：展示用标题 ----------
    // 后台配置的推荐产品标题基本都以 "SwitchBot " 开头（品牌前缀），
    // sidebar 已经在主品牌环境下，再重复展示品牌名既冗余又挤占有限的横向空间，
    // 因此前端展示时统一裁掉开头的 "SwitchBot" 及其后紧跟的空白字符。
    //
    // 实现要点：
    //   - 仅"开头"匹配（^SwitchBot），不影响标题中间出现的同名词汇
    //   - 大小写不敏感（/i），兼容 "Switchbot" / "SWITCHBOT" 等历史配置写法
    //   - 后跟 \s* 吃掉品牌名与产品名之间的空格 / 全角空格 / 制表符
    //   - 仅做去前缀，不再 trim 末尾，避免误伤设计上保留的尾部字符
    //   - prod.title 缺失时返回空串，避免模板渲染 "undefined"
    const getRecomProdTitle = (prod) => {
      const raw = (prod && prod.title) || '';
      return raw.replace(/^SwitchBot\s*/i, '');
    };

    // 产品缩略图 URL：兼容 storefront 与 Admin REST 两种数据格式 + 跟随选中变体切换
    //
    // 优先级设计：选中变体专属图 > 产品级主图 > 产品图集首张
    //   不优先用变体专属图，用户切下拉时图片不会刷新（产品级主图永远命中第一档兜底）
    //
    // 两种格式下的字段差异：
    //   storefront 格式（API 推荐返回值）
    //     variant.featured_image       字符串 URL 或 { src } 对象
    //     prod.featured_image          字符串 URL 或 { src } 对象
    //     prod.images[]                元素是字符串 URL 或 { src } 对象
    //   Admin REST 格式（metafield 当前数据）
    //     variant 上没有 featured_image，仅有 image_id 指向 prod.images 中某张的 id
    //     prod 上没有 featured_image，改名为 prod.image（{ id, src, ... } 对象）
    //     prod.images[] 元素一律为 { id, src, variant_ids, ... } 对象
    //
    // 查找顺序按"变体级 → 产品级 → 图集兜底"分层，每层兼容两种格式：
    //   1) variant.featured_image    （storefront）
    //   2) variant.image_id 在 prod.images 中按 id 查（Admin REST）
    //   3) prod.featured_image       （storefront）
    //   4) prod.image                 （Admin REST，注意字段名是单数 image，不是 featured_image）
    //   5) prod.images[0]            （所有格式的最后兜底）
    const getRecomProdImage = (prod) => {
      const variant = getRecomSelectedVariant(prod);
      // 1) 变体级 featured_image（storefront 格式）—— 切变体换图的关键路径
      if (variant && variant.featured_image) {
        if (typeof variant.featured_image === 'string') return variant.featured_image;
        if (variant.featured_image.src) return variant.featured_image.src;
      }
      // 2) 变体级 image_id（Admin REST 格式）—— 用 id 在 prod.images 里查到对应那张
      //    Admin REST 不在变体上挂图片对象，只挂一个指向 prod.images[].id 的引用
      if (variant && variant.image_id && Array.isArray(prod.images)) {
        const matched = prod.images.find(img => img && img.id === variant.image_id);
        if (matched && matched.src) return matched.src;
      }
      // 3) 产品级 featured_image（storefront 格式，字符串或 { src } 对象）
      if (typeof prod.featured_image === 'string') return prod.featured_image;
      if (prod.featured_image && prod.featured_image.src) return prod.featured_image.src;
      // 4) 产品级 image（Admin REST 格式，字段名单数 image，对象 { id, src, ... }）
      if (prod.image && prod.image.src) return prod.image.src;
      // 5) 兜底：产品图集第一张（字符串 / { src } 对象都兼容）
      if (Array.isArray(prod.images) && prod.images.length) {
        const first = prod.images[0];
        if (typeof first === 'string') return first;
        if (first && first.src) return first.src;
      }
      return '';
    };

    // 推荐列表价格格式化：兼容三种数据来源的单位差异
    //   /cart.js / /products/{handle}.js / /recommendations/products.json
    //                                       → number 整数 cents（例 898000，即 ¥8,980 × 100）
    //   /products.json                      → string 主单位带小数（例 "14980.00"）
    //   metafield Admin REST（当前 metafield 实际格式）
    //                                       → string 主单位无小数（例 "8980"，直接是日元）
    //
    // 旧实现按"字符串是否含 ." 区分主单位 vs cents 的写法在 metafield 切换到 Admin REST
    // 之后会出错——"8980" 这种主单位字符串没有小数点，会被误判成 cents → 除以 100 得 ¥90。
    // 改用 typeof 判断后定义清晰：
    //   typeof === 'number' → 一定是 storefront API 整数响应，必为 cents
    //   typeof === 'string' → 一定是主单位（带不带小数都通过 parseFloat 通吃）
    // 这样三种数据源都能正确显示原始日元金额。
    const formatRecomPrice = (price) => {
      if (price == null || price === '') return '';
      // number 类型 → cents（storefront API 一律整数响应）
      if (typeof price === 'number') {
        if (!Number.isFinite(price)) return '';
        return formatPrice(price);
      }
      // string 类型 → 主单位（含 "8980" / "14980.00" 两种写法），parseFloat 通吃
      const main = parseFloat(price);
      if (!Number.isFinite(main)) return '';
      return '¥' + Math.round(main).toLocaleString('ja-JP');
    };

    // 当前选中变体是否在打折：compare_at_price > price
    // - 两个字段都可能是字符串或数字，先 parseFloat 统一比较
    // - parseFloat("14980.00")=14980, parseFloat(1498000)=1498000，
    //   只要两者来自同一字段单位一致即可正确比较，不会因单位混淆得错
    const hasRecomDiscount = (prod) => {
      const variant = getRecomSelectedVariant(prod);
      if (!variant) return false;
      const cmp = parseFloat(variant.compare_at_price);
      const cur = parseFloat(variant.price);
      return Number.isFinite(cmp) && Number.isFinite(cur) && cmp > cur;
    };

    // ---------- 推荐产品：当前选中变体的"折扣徽章"（如 "20%OFF"）----------
    // 数据来源：customCartDiscountConfig（来自 metafields.custom.custom_key_jp_product_dc_config）
    //   结构：{
    //     [product_id]: {
    //       [variant_id]: {
    //         direct: {              // 直接折扣（推荐列表仅展示这一项）
    //           type: "direct",
    //           counponkey: "20%OFF",
    //           code: "",
    //           number: "20%OFF",    // ← 徽章展示文案，直接取此字段，无需任何转换
    //           desc: "...",         // 描述（前端不展示）
    //           label: "20%OFF",     // 标签（前端不展示）
    //           endtime: "2026-..."  // 截止时间（前端不展示）
    //         },
    //         point: {               // 积分兑换折扣（推荐列表不展示，预留给其他场景）
    //           type: "point",
    //           number: "会員限定23%OFF",
    //           ...
    //         }
    //       }
    //     }
    //   }
    //   注意：某个变体对象中可能仅存在 direct 或 point 之一，也可能两者都有；
    //         本函数仅关心 direct，没配 direct 时直接返回空串（整块徽章不渲染）
    //
    // 查询逻辑：
    //   1) 用产品 ID 取该产品的变体折扣表：优先 prod.product_id（API 返回时常见字段），
    //      没有再用 prod.id 兜底（部分元字段配置 API 直接以 variant 形态返回的产品对象）
    //   2) 用"当前选中变体 ID"取具体折扣项：与下拉框 select 切换联动，变体切换时徽章随之刷新
    //   3) 取 direct.number 作为徽章文案直接返回；缺失则返回空串（v-if 整块隐藏）
    //
    // 类型一致性：metafield JSON 里两级 key 都是字符串（"7703754211503"），但 prod.id /
    //   variant.id 在不同 API 来源下可能为 number，因此先 String() 转字符串再查 → 避免
    //   "对象属性按数值键存，但用字符串键找"造成漏命中
    const getRecomVariantDiscount = (prod) => {
      if (!prod) return '';
      const dcMap = customCartDiscountConfig.value || {};
      // 1) 取产品的变体折扣表：先用 product_id（推荐 API 标准字段），再用 id 兜底
      const prodKey = String(prod.product_id ?? prod.id ?? '');
      const prodDC = dcMap[prodKey] || {};
      // 2) 取选中变体折扣项：跟下拉切换的 recomSelectedVariantIds 联动
      const variant = getRecomSelectedVariant(prod);
      if (!variant || variant.id == null) return '';
      const variantKey = String(variant.id);
      const dc = prodDC[variantKey];
      // 3) 仅展示 direct.number；没配 direct / 没 number 时不渲染徽章
      if (!dc || !dc.direct || !dc.direct.number) return '';
      return dc.direct.number;
    };

    // 加购：把当前选中变体加入购物车
    // - 走 /cart/add.js → 自动被 fetch 劫持器捕获 →
    //   派发 myCusCart:AddStart（handleAddStart 开 sidebar + loading）→
    //   响应后派发 myCusCart:Update（handleUpdate refreshCart + 关 loading）
    // - 全局锁 isShowLoading：防止用户连点
    //   主动设为 true 是为了覆盖"hijack AddStart 派发前"的微小间隙
    // - 网络异常时主动关 loading：hijack 在 fetch 拒绝时不会派发 Update，否则锁会一直留着
    const addRecomProdToCart = async (prod) => {
      if (isShowLoading.value) return;
      const variant = getRecomSelectedVariant(prod);
      if (!variant || !variant.id) return;
      isShowLoading.value = true;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          // quantity:1 与设计稿一致——空购物车场景下推荐位仅做"发现 + 一键加 1 件"，
          // 数量调整由用户进入正常 line 列表后再操作
          body: JSON.stringify({ id: variant.id, quantity: 1 }),
        });
        // 不在此处置 false：handleUpdate 会 await refreshCart() 后置 false
      } catch (err) {
        console.log('addRecomProdToCart error:', err);
        isShowLoading.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('myCusCart:AddStart', handleAddStart);
      document.addEventListener('myCusCart:Update', handleUpdate);

      // 在 mask 上挂滚动拦截：外层页面无法滚动（详见上方 _onMaskWheel/TouchMove 注释）
      // mask 关闭时 visibility:hidden，监听自动哑火；mask 打开时盖满视口，吃掉所有滚动手势
      const mask = document.querySelector('.my-custom-cart-mask');
      if (mask) {
        mask.addEventListener('wheel', _onMaskWheel, { passive: false });
        mask.addEventListener('touchmove', _onMaskTouchMove, { passive: false });
      }
    });
    // 实际场景下 sidebar 与页面同生命周期，几乎不会卸载；
    // 加 unmount 清理是为了将来若改成动态挂载/HMR 时不留泄漏
    onUnmounted(() => {
      document.removeEventListener('myCusCart:AddStart', handleAddStart);
      document.removeEventListener('myCusCart:Update', handleUpdate);

      const mask = document.querySelector('.my-custom-cart-mask');
      if (mask) {
        mask.removeEventListener('wheel', _onMaskWheel);
        mask.removeEventListener('touchmove', _onMaskTouchMove);
      }
    });

    // 暴露到 window：
    //   - 第 4 种劫持（anchor click）需要在 Vue 上下文外调用 open
    //   - 后续头部购物车图标 / 其他自定义按钮也可直接 window.myCustomCartOpen()
    window.myCustomCartOpen = openCartFn;
    window.myCustomCartClose = closeCartFn;

    return {
      isShowCart,
      isShowLoading,
      customCartCommonConfig,
      customCartDiscountConfig,
      cartData,
      openCartFn,
      closeCartFn,
      formatPrice,
      getOriginalLinePrice,
      hasDiscount,
      isGiftItem,
      changeItemQuantity,
      onQtyInputBlur,
      removeItem,
      qtyErrors,
      // 底部结算栏
      couponCode,
      productDiscounts,
      productDiscountTotal,
      orderDiscounts,
      orderDiscountTotal,
      applyCouponFn,
      removeDiscountFn,
      goCheckoutFn,
      // 折扣进度条
      activeProgress,
      activeProgressItems,
      progressPercent,
      progressTip,
      isProgressNodeReached,
      getProgressNodePosition,
      // 推荐列表（おすすめIoT製品）
      // - defaultRecommendList / showDefaultRecommend：购物车空时的"配置文档来源"列表与显隐
      //   showDefaultRecommend 仍单独保留，给空状态文案的 has-recom 类用
      // - apiRecommendList：购物车非空时的"Shopify 推荐 API 来源"列表
      // - currentRecommendList / showRecommend：模板真正消费的统一入口
      //   currentRecommendList 内部按"购物车是否为空"切换数据源
      defaultRecommendList,
      showDefaultRecommend,
      apiRecommendList,
      currentRecommendList,
      showRecommend,
      recomSelectedVariantIds,
      shouldShowVariantSelect,
      isVariantAvailable,
      getRecomSelectedVariant,
      onRecomVariantChange,
      getRecomProdUrl,
      getRecomProdTitle,
      getRecomProdImage,
      formatRecomPrice,
      hasRecomDiscount,
      getRecomVariantDiscount,
      addRecomProdToCart,
    };
  },
}).mount('#myCustomCartBundleId');

})(); // [IIFE 包裹结束] 见文件顶部 "[IIFE 包裹]" 说明
